import { useCallback, useEffect, useState, FormEvent } from 'react';
import { apiClient } from '@/lib/api';
import { Assignment, AssignmentSubmission, Section } from '@/lib/types';

interface AssignmentManagerProps {
  courseId: number;
}

export default function AssignmentManager({ courseId }: AssignmentManagerProps) {
  const [assignments, setAssignments] = useState<Assignment[]>([]);
  const [sections, setSections] = useState<Section[]>([]);
  const [sectionId, setSectionId] = useState('');
  const [loading, setLoading] = useState(true);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [submissions, setSubmissions] = useState<Record<number, AssignmentSubmission[]>>({});
  const [gradeDraft, setGradeDraft] = useState<Record<number, string>>({});

  const load = useCallback(async () => {
    setLoading(true);
    const sectionsRes = await apiClient.getSections(courseId);
    if (sectionsRes.success && sectionsRes.data) {
      const items = Array.isArray(sectionsRes.data) ? sectionsRes.data : (sectionsRes.data as { results?: Section[] }).results || [];
      setSections(
        items
          .filter((s: Section) => s.course === courseId)
          .sort((a: Section, b: Section) => a.order - b.order)
      );
    }
    const res = await apiClient.getAssignments(courseId);
    if (res.success && res.data) {
      const items = (res.data as AssignmentResponseLike).results ?? res.data;
      const list = Array.isArray(items) ? items : [];
      setAssignments(list);
      const map: Record<number, AssignmentSubmission[]> = {};
      await Promise.all(
        list.map(async (assignment) => {
          const subRes = await apiClient.getAssignmentSubmissions(assignment.id);
          if (subRes.success && subRes.data) {
            const raw = (subRes.data as { results?: AssignmentSubmission[] }).results ?? subRes.data;
            map[assignment.id] = Array.isArray(raw) ? raw : [];
          }
        })
      );
      setSubmissions(map);
    } else {
      setError(res.error || 'Failed to load assignments');
    }
    setLoading(false);
  }, [courseId]);

  useEffect(() => {
    load();
  }, [load]);

  const handleCreate = async (e: FormEvent) => {
    e.preventDefault();
    if (!title.trim()) return;
    setSaving(true);
    setError(null);
    const res = await apiClient.createAssignment({
      course: courseId,
      section: sectionId ? Number(sectionId) : null,
      title: title.trim(),
      description: description.trim(),
    });
    setSaving(false);
    if (res.success) {
      setTitle('');
      setDescription('');
      setSectionId('');
      load();
    } else {
      setError(res.error || 'Failed to create assignment');
    }
  };

  const handleGrade = async (submissionId: number) => {
    const value = Number(gradeDraft[submissionId]);
    if (Number.isNaN(value) || value < 0 || value > 100) {
      alert('Enter a grade between 0 and 100');
      return;
    }
    const res = await apiClient.gradeAssignment(submissionId, value);
    if (res.success) {
      load();
    } else {
      alert(res.error || 'Failed to grade');
    }
  };

  return (
    <div className="space-y-6 max-w-3xl">
      <div>
        <h2 className="text-lg font-semibold text-slate-900">Assignments</h2>
        <p className="text-sm text-slate-500">
          Optional graded work. Students must meet the course pass mark on each assignment to earn a certificate.
        </p>
      </div>

      <form onSubmit={handleCreate} className="rounded-xl border border-slate-100 bg-white p-5 shadow-sm space-y-3">
        <input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Assignment title"
          className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
        />
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Instructions (optional)"
          rows={3}
          className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
        />
        <select
          value={sectionId}
          onChange={(e) => setSectionId(e.target.value)}
          className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
        >
          <option value="">Course-wide (no section)</option>
          {sections.map((section) => (
            <option key={section.id} value={section.id}>{section.title}</option>
          ))}
        </select>
        {error && <p className="text-sm text-red-600">{error}</p>}
        <button
          type="submit"
          disabled={saving || !title.trim()}
          className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
        >
          {saving ? 'Saving…' : 'Add assignment'}
        </button>
      </form>

      {loading ? (
        <div className="flex justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
        </div>
      ) : assignments.length === 0 ? (
        <p className="text-sm text-slate-400">No assignments yet. Courses can have exams, assignments, both, or neither.</p>
      ) : (
        <div className="space-y-4">
          {assignments.map((assignment) => (
            <div key={assignment.id} className="rounded-xl border border-slate-100 bg-white p-5 shadow-sm">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h3 className="font-semibold text-slate-900">{assignment.title}</h3>
                  <p className="mt-0.5 text-xs font-medium text-slate-500">
                    {assignment.section_title || 'Course-wide'}
                  </p>
                  {assignment.description && (
                    <p className="mt-1 text-sm text-slate-500 whitespace-pre-wrap">{assignment.description}</p>
                  )}
                  <p className="mt-1 text-xs text-slate-400">Pass mark: {assignment.pass_mark}%</p>
                </div>
                <button
                  type="button"
                  onClick={async () => {
                    if (!confirm('Delete this assignment?')) return;
                    const res = await apiClient.deleteAssignment(assignment.id);
                    if (res.success) load();
                    else alert(res.error || 'Delete failed');
                  }}
                  className="text-xs font-medium text-red-600 hover:text-red-800"
                >
                  Delete
                </button>
              </div>
              <div className="mt-4">
                <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-2">Submissions</h4>
                {(submissions[assignment.id] || []).length === 0 ? (
                  <p className="text-sm text-slate-400">No submissions yet.</p>
                ) : (
                  <ul className="divide-y divide-slate-100">
                    {(submissions[assignment.id] || []).map((sub) => (
                      <li key={sub.id} className="py-3 space-y-2">
                        <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
                          <span className="font-medium text-slate-800">{sub.student_username}</span>
                          <span className="text-xs text-slate-400">{sub.status}</span>
                        </div>
                        {sub.text && <p className="text-sm text-slate-600 whitespace-pre-wrap">{sub.text}</p>}
                        {sub.file && (
                          <a href={sub.file} target="_blank" rel="noreferrer" className="text-xs text-blue-600 underline">
                            Download file
                          </a>
                        )}
                        {sub.status === 'graded' && (
                          <p className="text-sm text-emerald-700">Grade: {sub.grade}%</p>
                        )}
                        <div className="flex gap-2">
                          <input
                            type="number"
                            min={0}
                            max={100}
                            value={gradeDraft[sub.id] ?? (sub.grade != null ? String(sub.grade) : '')}
                            onChange={(e) => setGradeDraft((prev) => ({ ...prev, [sub.id]: e.target.value }))}
                            className="w-24 rounded border border-slate-200 px-2 py-1 text-sm"
                            placeholder="Grade"
                          />
                          <button
                            type="button"
                            onClick={() => handleGrade(sub.id)}
                            className="rounded bg-emerald-600 px-3 py-1 text-xs font-semibold text-white hover:bg-emerald-700"
                          >
                            Save grade
                          </button>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

interface AssignmentResponseLike {
  results?: Assignment[];
}
